import React, { useEffect, useState } from "react";
import {
  Dialog,
  Box,
  IconButton,
  Tabs,
  Tab,
  Typography,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AbdmRecords from "../AbdmRecords/AbdmRecords";
import PastVisits from "../PastVisits/PastVisits";
import PatientSummaryModal from "./PatientSummaryModal";
import PatientAnalyticsModal from "./PatientAnalyticsModal";
import RadiologyReports from "./RadiologyReports";

const Patient360Modal = ({
  open,
  handleClose,
  orgCode,
  patientId,
  patientSummary,
  isLoadingSummary,
  totalRecords,
  abdmData,
  patientData,
  s3BucketFhir,
  care_context_list,
  selectedContextForRetrieveFhir,
  handleFhirArray,
  isAbdmEnabled,
  isAiEnabled,
}) => {
  const [activeTab, setActiveTab] = useState("Summary");
  const [showingMore, setShowingMore] = useState(false);
  const timelineBaseUrl =
    (process.env.REACT_APP_IS_TIMELINE_CONNECTED_WITH_PROD === "true"
      ? process.env.REACT_APP_TIMELINE_BASE_URL_PROD
      : process.env.REACT_APP_TIMELINE_BASE_URL_DEV) || "";


  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  const handleShowMore = () => {
    setShowingMore(!showingMore);
  };

  useEffect(() => {
    if (!isAiEnabled && activeTab === "Summary") {
      setActiveTab("Analytics");
    }
  }, [isAiEnabled, activeTab]);

  return (
    <Dialog
      open={open}
      handleClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          height: "95vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          pt: 2,
        }}
      >
        <Typography variant="h6" sx={{ color: "#125386", fontWeight: 600 }}>
          Patient 360
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ color: "#000" }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Divider under header */}
      <Box sx={{ borderBottom: "1px solid #C7C7CC", mt: 1, mx: 1 }} />

      {/* ---- Timeline Section (Reduced height) ---- */}
      <Box
        sx={{
          flex: showingMore ? "0 0 70%" : "0 0 15%",
          borderBottom: "1px solid #eee",
          overflowY: "auto",
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <iframe
            src={`${timelineBaseUrl}/timeline/?org=${orgCode}&patient_id=${patientId}`}
            width="97%"
            height="100%"
            style={{
              border: "none",
              maxWidth: "100%",
            }}
            title="Patient Timeline"
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          mt: 0.25,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleShowMore}
          sx={{
            borderRadius: "40px", // Rounded edges
            padding: "0px 6px", // Smaller padding to shrink the button
            textTransform: "none", // Keep text lowercase
            fontSize: "0.75rem", // Smaller font size
            color: "#757575", // Gray text color
            borderColor: "#757575", // Gray border
            "&:hover": {
              borderColor: "#757575", // Keep the border color on hover
              backgroundColor: "transparent", // No background color on hover
            },
          }}
        >
          {showingMore ? "Show less records" : "Show more records"}
        </Button>
      </Box>

      {/* ---- Tabs Section (Increased height + scrollable content) ---- */}
      <Box
        sx={{
          flex: showingMore ? "0 0 30%" : "0 0 85%",
          display: "flex",
          flexDirection: "column",
          mx: 1,
          mt: 1,
          minHeight: 0, // crucial for scroll
        }}
      >
        {/* Tabs Header (Fixed height, new design) */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          textColor="inherit"
          TabIndicatorProps={{ style: { display: "none" } }}
          sx={{
            flex: "0 0 45px", // smaller height (~80% of old)
            minHeight: "45px",
            backgroundColor: "#FFFFFF",
            borderBottom: "1px solid #BCBEC0",
            gap: "2px",
          }}
        >
          {["Summary", "Analytics", "ABDM", "Local", "Radiology Reports"]
            .filter(
              (tab) =>
                (isAiEnabled || tab !== "Summary") &&
                (isAbdmEnabled || tab !== "ABDM")
            ) // Exclude ABDM if ABDMEnabled is false, hide Summary if AI disabled
            .map((tab) => (
              <Tab
                key={tab}
                label={tab}
                value={tab}
                disableRipple
                sx={{
                  fontWeight: 700,
                  fontSize: "0.9rem",
                  textTransform: "none",
                  minHeight: "40px",
                  border:
                    activeTab === tab
                      ? "1px solid #0E3F66"
                      : "1px solid #D9D9D9",
                  borderRadius: "6px",
                  mx: 0,
                  bgcolor: activeTab === tab ? "#125386" : "#FFFFFF",
                  color: activeTab === tab ? "#FFFFFF" : "#171717",
                  transition: "all 0.2s ease-in-out",
                }}
              />
            ))}
        </Tabs>

        {/* Scrollable Content (fills remaining area properly) */}
        <Box
          sx={{
            flex: "1 1 auto",
            minHeight: 0,
            overflowY: "auto",
            p: 2,
            WebkitOverflowScrolling: "touch",
            position: "relative",
            pb: 4,
          }}
        >
          {isAiEnabled && activeTab === "Summary" && (
            <Box sx={{ pb: 4 }}>
              <PatientSummaryModal
                patientSummary={patientSummary}
                totalRecords={totalRecords}
                isLoadingSummary={isLoadingSummary}
              />
            </Box>
          )}

          {activeTab === "Analytics" && (
            <Box sx={{ pb: 4 }}>
              <PatientAnalyticsModal orgCode={orgCode} patientId={patientId} />
            </Box>
          )}

          {activeTab === "ABDM" && (
            <Box sx={{ pb: 4 }}>
              {" "}
              {/* Prevent ABDM content from touching bottom */}
              <AbdmRecords organizedArray={abdmData} />
            </Box>
          )}

          {activeTab === "Local" && (
            <Box
              sx={{
                pb: 4, // ensures bottom padding for scrolling visibility
                overflow: "visible", // don't restrict content
                position: "relative",
              }}
            >
              <PastVisits
                patientData={patientData}
                fhirArrayData={s3BucketFhir}
                careContextList={care_context_list}
                selectedCareContext={selectedContextForRetrieveFhir}
                onSubmitFhirArray={handleFhirArray}
              />
            </Box>
          )}

          {activeTab === "Radiology Reports" && (
            <Box sx={{ pb: 4 }}>
              <RadiologyReports patientId={patientId} />
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default Patient360Modal;
